import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { ASSETS, CLINIC } from "@/lib/assets";

// At md (768–1023) we show a slimmer 3-link nav.
// At lg (>=1024) we show the full 5-link nav.
const primaryLinks = [
    { label: "Doctor", href: "#doctor" },
    { label: "Expertise", href: "#expertise" },
    { label: "Consult", href: "#consult" },
];
const extraLinks = [
    { label: "Space", href: "#space" },
    { label: "Voices", href: "#voices" },
];

export const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <>
            <motion.header
                initial={{ y: -30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 1, delay: 1.6, ease: [0.76, 0, 0.24, 1] }}
                className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
                    scrolled || open
                        ? "bg-[#F9F8F6]/92 backdrop-blur-xl border-b border-surya-line"
                        : "bg-transparent"
                }`}
                data-testid="site-nav"
            >
                <div className="mx-auto max-w-[1600px] px-4 md:px-8 lg:px-10 flex items-center justify-between h-[64px] md:h-[68px] lg:h-[72px]">
                    <a
                        href="#top"
                        className="flex items-center gap-2 md:gap-3 min-w-0"
                        data-testid="nav-logo"
                    >
                        <img
                            src={ASSETS.logo}
                            alt="Surya Dental Care"
                            className="h-9 w-9 md:h-10 md:w-10 lg:h-11 lg:w-11 object-contain shrink-0"
                        />
                        <div className="flex flex-col leading-none min-w-0">
                            <span className="font-serif text-[13px] md:text-[14px] lg:text-[15px] font-medium tracking-tight text-surya-ink truncate">
                                Surya Dental Care
                            </span>
                            <span className="hidden lg:block label text-surya-muted mt-0.5 text-xs">
                                Dibdih · Ranchi
                            </span>
                        </div>
                    </a>

                    <nav className="hidden md:flex items-center gap-5 lg:gap-10">
                        {primaryLinks.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                data-testid={`nav-link-${l.label.toLowerCase()}`}
                                className="label text-surya-ink hover:text-surya-orange text-[0.65rem] lg:text-xs"
                            >
                                {l.label}
                            </a>
                        ))}
                        {extraLinks.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                data-testid={`nav-link-${l.label.toLowerCase()}`}
                                className="hidden lg:inline-block label text-surya-ink hover:text-surya-orange text-xs"
                            >
                                {l.label}
                            </a>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2 md:gap-3">
                        <a
                            href={`tel:${CLINIC.phoneE164}`}
                            data-testid="nav-phone"
                            className="hidden md:inline-flex items-center gap-2 border border-surya-line hover:border-surya-orange hover:text-surya-orange rounded-full px-3 md:px-4 py-2 md:py-2.5 label text-surya-ink text-[0.65rem] lg:text-xs"
                        >
                            <Phone className="h-3 w-3 md:h-3.5 md:w-3.5" strokeWidth={1.4} />
                            {CLINIC.phone}
                        </a>
                        <a
                            href={CLINIC.whatsapp}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="nav-book-button"
                            className="group hidden sm:inline-flex items-center gap-2 rounded-full bg-surya-orange text-white hover:bg-surya-orangeHover pl-3.5 md:pl-4 lg:pl-5 pr-3.5 md:pr-4 lg:pr-5 py-2 md:py-2.5 label text-[0.65rem] lg:text-xs"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-white" />
                            Book
                        </a>
                        <button
                            className="md:hidden inline-flex h-10 w-10 items-center justify-center border border-surya-line rounded-full text-surya-ink hover:bg-surya-surface"
                            onClick={() => setOpen((v) => !v)}
                            aria-label="Toggle menu"
                            data-testid="nav-menu-toggle"
                        >
                            {open ? <X className="h-4 w-4" strokeWidth={1.5} /> : <Menu className="h-4 w-4" strokeWidth={1.5} />}
                        </button>
                    </div>
                </div>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="fixed inset-0 z-40 md:hidden bg-surya-bg pt-[64px]"
                        data-testid="mobile-menu"
                    >
                        <div className="px-5 pt-8 pb-8 flex flex-col gap-2 border-b border-surya-line">
                            {[...primaryLinks, ...extraLinks].map((l, i) => (
                                <motion.a
                                    key={l.href}
                                    href={l.href}
                                    onClick={() => setOpen(false)}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.05 + i * 0.06, duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
                                    className="group flex items-baseline justify-between border-b border-surya-line py-4"
                                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                                >
                                    <span className="font-serif text-4xl font-light tracking-[-0.02em] text-surya-ink group-hover:text-surya-orange">
                                        {l.label}
                                    </span>
                                    <span className="label text-surya-muted">
                                        /0{i + 1}
                                    </span>
                                </motion.a>
                            ))}
                        </div>
                        <div className="px-5 pt-6 flex gap-3">
                            <a
                                href={CLINIC.whatsapp}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-surya-orange text-white py-4 label"
                                onClick={() => setOpen(false)}
                            >
                                Book on WhatsApp
                            </a>
                            <a
                                href={`tel:${CLINIC.phoneE164}`}
                                className="inline-flex items-center justify-center gap-2 rounded-full border border-surya-line px-5 py-4 label text-surya-ink"
                                onClick={() => setOpen(false)}
                            >
                                Call
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
